import { Wrapper as SpinnerWrapper } from "../spinner/spinner.styles";
import { createShouldForwardPropBlockList, deepMerge } from "@components/common/utils/helper";
import { type ButtonProps } from "./button";
import {
    getArrowBeforeBackgroundColor,
    getArrowSpanBeforeBackgroundColor,
    getBackgroundColor,
    getBorderColor,
    getBorderRadius,
    getBoxShadow,
    getBoxShadowFocus,
    getColor,
    getFontSize,
    getAdornmentsSvgSize,
    getPaddingX,
    getPaddingY,
    getSpinnerBorderColor,
    getBodyHeight,
} from "./button.styles.utils";
import styled from "styled-components";

type WrapperProps = Pick<
    ButtonProps,
    | "outline"
    | "text"
    | "disableHeight"
    | "outlineKeepOnHover"
    | "underline"
    | "active"
    | "focusState"
    | "label"
    | "arrow"
    | "size"
    | "keepColor"
    | "length"
    | "rounded"
    | "color"
    | "block"
    | "shadow"
    | "fillIcon"
    | "largeIcon"
    | "clean"
    | "reflectX"
    | "disabled"
>;
type BodyProps = Pick<ButtonProps, "size" | "text" | "disableHeight">;
type AdornmentsProps = Pick<ButtonProps, "size">;
type LabelProps = {
    ghost?: boolean | false;
};

export const Wrapper = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList([
        "outline",
        "underline",
        "outlineKeepOnHover",
        "color",
        "clean",
        "size",
        "rounded",
        "arrow",
        "length",
        "shadow",
        "reflectX",
        "largeIcon",
        "fillIcon",
        "block",
        "label",
        "text",
        "disableHeight",
        "keepColor",
        "active",
        "focusState",
    ]),
})<WrapperProps>(
    ({
        size,
        underline,
        active,
        focusState,
        text,
        disableHeight,
        length,
        rounded,
        outline,
        outlineKeepOnHover,
        color,
        label,
        block,
        shadow,
        fillIcon,
        largeIcon,
        clean,
        keepColor,
        reflectX,
        disabled,
        theme,
    }) => ({
        transitionProperty: "background-color, box-shadow, color, opacity, transform, border",
        transitionDuration: "300ms",
        transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
        textDecoration: "none !important",
        display: "inline-flex",
        position: "relative",
        fontWeight: theme.fontWeight.medium,
        cursor: "pointer",
        whiteSpace: "nowrap",
        justifyContent: "center",
        ...(!(text && disableHeight) && {
            lineHeight: size === "xs" ? theme.lineHeight[5] : theme.lineHeight[6],
        }),
        ...(!fillIcon && {
            borderStyle: "solid",
            ...(!(text && disableHeight) && {
                borderWidth: theme.borderWidth[2],
            }),
        }),
        ...((text || underline) && { borderLeft: "none", borderRight: "none" }),
        ...(reflectX && { scale: "-1 1" }),
        ...(disabled && {
            pointerEvents: "none",
            opacity: 0.5,
        }),
        ...deepMerge(
            getBackgroundColor(active, text, outline, underline, outlineKeepOnHover, color, fillIcon, clean, theme),
            getColor(active, text, outline, underline, outlineKeepOnHover, color, fillIcon, clean, keepColor, theme),
            getBorderColor(active, text, color, clean, theme),
            getFontSize(size, theme),
            getPaddingX(underline, size, label, text, fillIcon, clean, theme),
            getPaddingY(underline, size, fillIcon, text, disableHeight, theme),
            getBorderRadius(rounded, theme),
            getBoxShadow(shadow, color, theme),
        ),
        ...(underline && {
            borderTopColor: "transparent",
            borderRightColor: "transparent",
            borderLeftColor: "transparent",
        }),
        minWidth: length ? `${length}px` : "auto",
        ...(block && { width: "100%" }),
        "&:hover": {
            ...deepMerge(
                getBackgroundColor(true, text, outline, underline, outlineKeepOnHover, color, fillIcon, clean, theme),
                getColor(true, text, outline, underline, outlineKeepOnHover, color, fillIcon, clean, keepColor, theme),
                getBorderColor(true, text, color, clean, theme),
            ),
            ...(underline && {
                borderTopColor: "transparent",
                borderRightColor: "transparent",
                borderLeftColor: "transparent",
            }),
            [`${Arrow}`]: {
                "&:before": {
                    ...deepMerge(getArrowBeforeBackgroundColor(true, text, outline, underline, outlineKeepOnHover, color, keepColor, theme)),
                    translate: `${theme.spacing[1]} -${theme.spacing[0.5]}`,
                },
                "&:after": {
                    ...deepMerge(getArrowBeforeBackgroundColor(true, text, outline, underline, outlineKeepOnHover, color, keepColor, theme)),
                    translate: `${theme.spacing[1]} ${theme.spacing[0.5]}`,
                },
                "span:before": {
                    width: theme.spacing[2],
                    opacity: 1,
                    ...deepMerge(getArrowSpanBeforeBackgroundColor(true, text, outline, underline, outlineKeepOnHover, color, keepColor, theme)),
                },
            },
        },
        "&:disabled": {
            pointerEvents: "none",
            opacity: 0.5,
        },
        "&:focus": {
            zIndex: 10,
            outline: "none",
            ...deepMerge(getBoxShadowFocus(color, text, underline, fillIcon, clean, focusState, theme)),
            ...(focusState === "hover" && {
                ...deepMerge(
                    getBackgroundColor(true, text, outline, underline, outlineKeepOnHover, color, fillIcon, clean, theme),
                    getColor(true, text, outline, underline, outlineKeepOnHover, color, fillIcon, clean, keepColor, theme),
                    getBorderColor(true, text, color, clean, theme),
                ),
                ...(underline && {
                    borderTopColor: "transparent",
                    borderRightColor: "transparent",
                    borderLeftColor: "transparent",
                }),
            }),
        },
        [`${Arrow}`]: {
            "&:before, &:after": {
                ...deepMerge(getArrowBeforeBackgroundColor(active, text, outline, underline, outlineKeepOnHover, color, keepColor, theme)),
            },
            "span:before": {
                ...deepMerge(getArrowSpanBeforeBackgroundColor(active, text, outline, underline, outlineKeepOnHover, color, keepColor, theme)),
            },
        },
        [`${Adornments}`]: {
            ...(largeIcon && { scale: "125%" }),
        },
        ...(!keepColor && {
            [`${SpinnerWrapper}`]: {
                ...deepMerge(getSpinnerBorderColor(active, text, outline, underline, outlineKeepOnHover, color, keepColor, theme)),
            },
        }),
        "&.swiper-button-disabled": {
            pointerEvents: "none",
            opacity: 0.25,
        },
        "&.swiper-button-lock": {
            pointerEvents: "none",
            opacity: 0,
        },
    }),
);

export const Body = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size", "text", "disableHeight"]),
})<BodyProps>(({ size, text, disableHeight, theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing[2],
    minWidth: size === "xs" ? theme.spacing[5] : theme.spacing[6],
    ...deepMerge(getBodyHeight(size, text, disableHeight, theme)),
}));

export const Label = styled("div")<LabelProps>(({ ghost }) => ({
    ...(ghost && {
        opacity: 0,
        width: "0",
        overflow: "hidden",
        margin: "0",
    }),
    [`${Arrow}`]: {
        ...(ghost && {
            margin: "0",
        }),
    },
}));

export const Adornments = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size"]),
})<AdornmentsProps>(({ size, theme }) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
        ...deepMerge(getAdornmentsSvgSize(size, theme)),
    },
}));

export const Loader = styled("div")(() => ({}));

export const Arrow = styled("div")(({ theme }) => ({
    width: theme.spacing[2],
    height: theme.spacing[4],
    position: "relative",
    marginLeft: `-${theme.spacing[1]}`,
    "&:only-child": {
        marginLeft: 0,
    },
    "&:before, &:after": {
        content: '""',
        position: "absolute",
        left: 0,
        width: "7px",
        height: theme.spacing[0.5],
        top: "50%",
        marginTop: `-${theme.spacing.px}`,
        transitionProperty: "transform, translate, width, height, opacity",
        transitionDuration: "300ms",
        transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
        borderRadius: theme.borderRadius.full,
    },
    "&:before": {
        rotate: "45deg",
        translate: `0 -${theme.spacing[0.5]}`,
    },
    "&:after": {
        rotate: "-45deg",
        translate: `0 ${theme.spacing[0.5]}`,
    },
    span: {
        width: theme.spacing[2],
        height: theme.spacing[4],
        position: "absolute",
        left: 0,
        top: 0,
        display: "block",
    },
    "span:before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "50%",
        width: "3px",
        height: theme.spacing[0.5],
        marginTop: `-${theme.spacing.px}`,
        transitionProperty: "transform, translate, width, height, opacity",
        transitionDuration: "300ms",
        transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
        opacity: 0,
        borderRadius: theme.borderRadius.full,
    },
}));

export const Counter = styled("div")(() => ({}));

export const File = styled("input")(() => ({}));
